import React, { useState, useMemo } from 'react';
import { BookOpen, Zap, Brain, CircleCheck, Star, Menu, X, ArrowRight, Sparkles, Trophy, Target, ChevronDown } from 'lucide-react';
import { motion } from 'motion/react';
import SourcityLogo from '../../imports/SourcityLogoWordmarkIconEmbedded1';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { Button } from './ui/button';
import { LanguageToggle } from './language-toggle';
import { usePublicPricing, formatPlanPrice } from '../../hooks/use-public-pricing';
import { useI18n } from '../../i18n';
import { BillingPricingStatus } from './billing-pricing-status';
import type { BillingPlan } from '../../billing/checkout-return';

interface LandingPageProps {
  onGetStarted: (plan?: BillingPlan) => void;
}

export function LandingPage({ onGetStarted }: LandingPageProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const { pricing, loading, error, retry } = usePublicPricing();
  const { t, tList } = useI18n();

  const monthlyDisplay = useMemo(
    () =>
      pricing
        ? formatPlanPrice(pricing.monthlyAmount, pricing.currency)
        : '—',
    [pricing]
  );
  const annualDisplay = useMemo(
    () =>
      pricing
        ? formatPlanPrice(pricing.annualAmount, pricing.currency)
        : '—',
    [pricing]
  );
  const faqs = ['q1', 'q2', 'q3', 'q4', 'q5'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-[#F5F5F7] to-white">
      {/* Navigation */}
      <nav className="fixed top-0 start-0 end-0 z-50 bg-white/80 backdrop-blur-xl border-b border-white/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <div className="w-32 pt-1 overflow-visible">
                <SourcityLogo />
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm text-[#434343] hover:text-[#7367FF] transition-colors">{t('landing.nav.features')}</a>
              <a href="#how-it-works" className="text-sm text-[#434343] hover:text-[#7367FF] transition-colors">{t('landing.nav.howItWorks')}</a>
              <a href="#pricing" className="text-sm text-[#434343] hover:text-[#7367FF] transition-colors">{t('landing.nav.pricing')}</a>
              <a href="#faq" className="text-sm text-[#434343] hover:text-[#7367FF] transition-colors">{t('landing.nav.faq')}</a>
              <LanguageToggle />
              <Button
                onClick={() => onGetStarted()}
                className="bg-gradient-to-r from-[#7367FF] to-[#2DD2FE] hover:opacity-90 text-white shadow-lg shadow-[#7367FF]/30"
              >
                {t('landing.nav.getStarted')}
              </Button>
            </div>

            {/* Mobile: language toggle stays visible next to the menu button */}
            <div className="flex items-center gap-2 md:hidden">
              <LanguageToggle variant="compact" />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {mobileMenuOpen ? <X className="h-6 w-6 text-[#434343]" /> : <Menu className="h-6 w-6 text-[#434343]" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="md:hidden py-4 space-y-3 border-t border-gray-200"
            >
              <a href="#features" className="block px-4 py-2 text-sm text-[#434343] hover:bg-gray-50 rounded-lg">{t('landing.nav.features')}</a>
              <a href="#how-it-works" className="block px-4 py-2 text-sm text-[#434343] hover:bg-gray-50 rounded-lg">{t('landing.nav.howItWorks')}</a>
              <a href="#pricing" className="block px-4 py-2 text-sm text-[#434343] hover:bg-gray-50 rounded-lg">{t('landing.nav.pricing')}</a>
              <a href="#faq" className="block px-4 py-2 text-sm text-[#434343] hover:bg-gray-50 rounded-lg">{t('landing.nav.faq')}</a>
              <Button
                onClick={() => onGetStarted()}
                className="w-full bg-gradient-to-r from-[#7367FF] to-[#2DD2FE] hover:opacity-90 text-white shadow-lg shadow-[#7367FF]/30"
              >
                {t('landing.nav.getStarted')}
              </Button>
            </motion.div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Leading Column - Text */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#7367FF]/10 to-[#2DD2FE]/10 border border-[#7367FF]/20">
                <Sparkles className="h-4 w-4 text-[#7367FF]" />
                <span className="text-sm font-medium text-[#7367FF]">{t('landing.hero.badge')}</span>
              </div>

              <h1 className="text-5xl lg:text-6xl font-display leading-tight bg-gradient-to-r from-[#434343] via-[#7367FF] to-[#2DD2FE] text-transparent bg-clip-text">
                {t('landing.hero.title')}
              </h1>

              <p className="text-lg text-[#434343]/80 leading-relaxed">
                {t('landing.hero.subtitle')}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() => onGetStarted()}
                  className="bg-gradient-to-r from-[#7367FF] to-[#2DD2FE] hover:opacity-90 text-white shadow-lg shadow-[#7367FF]/30 h-12 px-8 text-base"
                >
                  {t('landing.hero.primaryCta')}
                  <ArrowRight className="h-5 w-5 ms-2 rtl-flip" />
                </Button>
                <Button
                  variant="outline"
                  className="h-12 px-8 text-base border-2 border-[#7367FF]/20 hover:bg-[#7367FF]/5"
                >
                  {t('landing.hero.secondaryCta')}
                </Button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 pt-8 border-t border-gray-200/50">
                <div>
                  <p className="text-3xl font-display text-[#7367FF]">10K+</p>
                  <p className="text-sm text-[#434343]/60">{t('landing.hero.stats.students')}</p>
                </div>
                <div>
                  <p className="text-3xl font-display text-[#7367FF]">500K+</p>
                  <p className="text-sm text-[#434343]/60">{t('landing.hero.stats.quizzes')}</p>
                </div>
                <div>
                  <p className="text-3xl font-display text-[#7367FF]">95%</p>
                  <p className="text-sm text-[#434343]/60">{t('landing.hero.stats.successRate')}</p>
                </div>
              </div>
            </motion.div>

            {/* Trailing Column - Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-[#7367FF]/20">
                <ImageWithFallback
                  src="https://images.unsplash.com/photo-1701576766277-c6160505581d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzdHVkZW50JTIwc3R1ZHlpbmclMjBsYXB0b3B8ZW58MXx8fHwxNzY2Nzg4NjA5fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                  alt={t('landing.hero.imageAlt')}
                  className="w-full h-auto"
                />
                {/* Floating Card */}
                <div className="absolute bottom-6 start-6 end-6 bg-white/90 backdrop-blur-xl rounded-2xl p-4 shadow-xl border border-white/40">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#7367FF] to-[#2DD2FE] flex items-center justify-center">
                      <Brain className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[#434343]">{t('landing.hero.floatingCardTitle')}</p>
                      <p className="text-xs text-[#434343]/60">{t('landing.hero.floatingCardSubtitle')}</p>
                    </div>
                    <CircleCheck className="h-6 w-6 text-[#3CFF9B]" />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6 lg:px-8 bg-white/40">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-display mb-4 bg-gradient-to-r from-[#7367FF] to-[#2DD2FE] text-transparent bg-clip-text">
              {t('landing.features.title')}
            </h2>
            <p className="text-lg text-[#434343]/80 max-w-2xl mx-auto">
              {t('landing.features.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard
              icon={Brain}
              title={t('landing.features.quizzes.title')}
              description={t('landing.features.quizzes.description')}
              color="from-[#7367FF] to-[#2DD2FE]"
            />
            <FeatureCard
              icon={BookOpen}
              title={t('landing.features.reader.title')}
              description={t('landing.features.reader.description')}
              color="from-[#2DD2FE] to-[#3CFF9B]"
            />
            <FeatureCard
              icon={Target}
              title={t('landing.features.personalized.title')}
              description={t('landing.features.personalized.description')}
              color="from-[#7367FF] to-[#3CFF9B]"
            />
            <FeatureCard
              icon={Zap}
              title={t('landing.features.feedback.title')}
              description={t('landing.features.feedback.description')}
              color="from-[#3CFF9B] to-[#2DD2FE]"
            />
            <FeatureCard
              icon={Trophy}
              title={t('landing.features.progress.title')}
              description={t('landing.features.progress.description')}
              color="from-[#2DD2FE] to-[#7367FF]"
            />
            <FeatureCard
              icon={Sparkles}
              title={t('landing.features.notes.title')}
              description={t('landing.features.notes.description')}
              color="from-[#3CFF9B] to-[#7367FF]"
            />
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-display mb-4 bg-gradient-to-r from-[#7367FF] to-[#2DD2FE] text-transparent bg-clip-text">
              {t('landing.howItWorks.title')}
            </h2>
            <p className="text-lg text-[#434343]/80 max-w-2xl mx-auto">
              {t('landing.howItWorks.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-12">
            <StepCard
              number="01"
              title={t('landing.howItWorks.step1.title')}
              description={t('landing.howItWorks.step1.description')}
              image="https://images.unsplash.com/photo-1623679116710-78b05d2fe2f3?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjB3b3Jrc3BhY2UlMjBkZXNrfGVufDF8fHx8MTc2NjczNjc2Nnww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
            />
            <StepCard
              number="02"
              title={t('landing.howItWorks.step2.title')}
              description={t('landing.howItWorks.step2.description')}
              image="https://images.unsplash.com/photo-1758270704384-9df36d94a29d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx1bml2ZXJzaXR5JTIwc3R1ZGVudHMlMjBsZWFybmluZ3xlbnwxfHx8fDE3NjY3NTg1NDd8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
            />
            <StepCard
              number="03"
              title={t('landing.howItWorks.step3.title')}
              description={t('landing.howItWorks.step3.description')}
              image="https://images.unsplash.com/photo-1701576766277-c6160505581d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzdHVkZW50JTIwc3R1ZHlpbmclMjBsYXB0b3B8ZW58MXx8fHwxNzY2Nzg4NjA5fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
            />
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-6 lg:px-8 bg-white/40">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-display mb-4 bg-gradient-to-r from-[#7367FF] to-[#2DD2FE] text-transparent bg-clip-text">
              {t('landing.pricing.title')}
            </h2>
            <p className="text-lg text-[#434343]/80 max-w-2xl mx-auto">
              {t('landing.pricing.subtitle')}
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto"><BillingPricingStatus loading={loading} error={error} retry={retry} /></div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <PricingCard
              name={t('landing.pricing.monthly.name')}
              price={monthlyDisplay}
              period={t('landing.pricing.perMonth')}
              description={t('landing.pricing.monthly.description')}
              features={tList('landing.pricing.monthly.features')}
              buttonText={t('landing.pricing.monthly.cta')}
              popularLabel={t('landing.pricing.mostPopular')}
              onButtonClick={() => onGetStarted('monthly')}
              disabled={!pricing || loading}
              highlighted
            />
            <PricingCard
              name={t('landing.pricing.annual.name')}
              price={annualDisplay}
              period={t('landing.pricing.perYear')}
              description={t('landing.pricing.annual.description')}
              features={tList('landing.pricing.annual.features')}
              buttonText={t('landing.pricing.annual.cta')}
              popularLabel={t('landing.pricing.mostPopular')}
              onButtonClick={() => onGetStarted('annual')}
              disabled={!pricing || loading}
              highlighted
            />
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-display mb-4 bg-gradient-to-r from-[#7367FF] to-[#2DD2FE] text-transparent bg-clip-text">
              {t('landing.testimonials.title')}
            </h2>
            <p className="text-lg text-[#434343]/80 max-w-2xl mx-auto">
              {t('landing.testimonials.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {(['one', 'two', 'three'] as const).map((key) => (
              <TestimonialCard
                key={key}
                quote={t(`landing.testimonials.${key}.quote`)}
                author={t(`landing.testimonials.${key}.author`)}
                role={t(`landing.testimonials.${key}.role`)}
                rating={5}
              />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-6 lg:px-8 bg-white/40">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-display mb-4 bg-gradient-to-r from-[#7367FF] to-[#2DD2FE] text-transparent bg-clip-text">
              {t('landing.faq.title')}
            </h2>
            <p className="text-lg text-[#434343]/80">
              {t('landing.faq.subtitle')}
            </p>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((key, idx) => (
              <FAQItem
                key={key}
                question={t(`landing.faq.${key}.question`)}
                answer={t(`landing.faq.${key}.answer`)}
                isOpen={openFaq === idx}
                onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-[#7367FF] to-[#2DD2FE] rounded-3xl p-12 text-center shadow-2xl shadow-[#7367FF]/30"
          >
            <h2 className="text-4xl lg:text-5xl font-display text-white mb-4">
              {t('landing.cta.title')}
            </h2>
            <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
              {t('landing.cta.subtitle')}
            </p>
            <Button
              onClick={() => onGetStarted()}
              className="bg-white hover:bg-gray-50 text-[#7367FF] shadow-lg h-12 px-8 text-base font-medium"
            >
              {t('landing.cta.button')}
              <ArrowRight className="h-5 w-5 ms-2 rtl-flip" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#434343] text-white pt-12 pb-[calc(6rem+env(safe-area-inset-bottom,0px))] px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="w-32 mb-4 brightness-0 invert">
                <SourcityLogo />
              </div>
              <p className="text-sm text-white/60">
                {t('landing.footer.tagline')}
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-4">{t('landing.footer.product')}</h3>
              <ul className="space-y-2 text-sm text-white/60">
                <li><a href="#features" className="hover:text-white transition-colors">{t('landing.footer.features')}</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">{t('landing.footer.pricing')}</a></li>
                <li><a href="#faq" className="hover:text-white transition-colors">{t('landing.footer.faq')}</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">{t('landing.footer.company')}</h3>
              <ul className="space-y-2 text-sm text-white/60">
                <li><a href="#" className="hover:text-white transition-colors">{t('landing.footer.about')}</a></li>
                <li><a href="#" className="hover:text-white transition-colors">{t('landing.footer.blog')}</a></li>
                <li><a href="#" className="hover:text-white transition-colors">{t('landing.footer.careers')}</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">{t('landing.footer.legal')}</h3>
              <ul className="space-y-2 text-sm text-white/60">
                <li><a href="#" className="hover:text-white transition-colors">{t('landing.footer.privacy')}</a></li>
                <li><a href="#" className="hover:text-white transition-colors">{t('landing.footer.terms')}</a></li>
                <li><a href="#" className="hover:text-white transition-colors">{t('landing.footer.cookies')}</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 text-center text-sm text-white/60">
            <p>{t('landing.footer.rights')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Feature Card Component
interface FeatureCardProps {
  icon: React.ElementType;
  title: string;
  description: string;
  color: string;
}

function FeatureCard({ icon: Icon, title, description, color }: FeatureCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="bg-white/60 backdrop-blur-xl border border-white/40 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all hover:scale-[1.02]"
    >
      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${color} flex items-center justify-center mb-4 shadow-lg`}>
        <Icon className="h-7 w-7 text-white" />
      </div>
      <h3 className="text-xl font-display text-[#434343] mb-2">{title}</h3>
      <p className="text-sm text-[#434343]/70">{description}</p>
    </motion.div>
  );
}

// Step Card Component
interface StepCardProps {
  number: string;
  title: string;
  description: string;
  image: string;
}

function StepCard({ number, title, description, image }: StepCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="relative"
    >
      <div className="relative rounded-2xl overflow-hidden mb-6 shadow-lg">
        <ImageWithFallback
          src={image}
          alt={title}
          className="w-full h-48 object-cover"
        />
        <div className="absolute top-4 start-4 w-12 h-12 rounded-full bg-gradient-to-br from-[#7367FF] to-[#2DD2FE] flex items-center justify-center shadow-lg">
          <span className="text-xl font-display text-white">{number}</span>
        </div>
      </div>
      <h3 className="text-2xl font-display text-[#434343] mb-2">{title}</h3>
      <p className="text-sm text-[#434343]/70">{description}</p>
    </motion.div>
  );
}

// Pricing Card Component
interface PricingCardProps {
  name: string;
  price: string;
  period?: string;
  description: string;
  features: string[];
  buttonText: string;
  popularLabel: string;
  onButtonClick: () => void;
  highlighted?: boolean;
  disabled?: boolean;
}

function PricingCard({ name, price, period, description, features, buttonText, popularLabel, onButtonClick, highlighted, disabled }: PricingCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`bg-white/60 backdrop-blur-xl border rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all ${
        highlighted
          ? 'border-[#7367FF] ring-2 ring-[#7367FF]/20 scale-105'
          : 'border-white/40'
      }`}
    >
      {highlighted && (
        <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-[#7367FF] to-[#2DD2FE] text-white text-xs font-medium mb-4">
          <Star className="h-3 w-3" />
          {popularLabel}
        </div>
      )}
      <h3 className="text-2xl font-display text-[#434343] mb-2">{name}</h3>
      <p className="text-sm text-[#434343]/60 mb-4">{description}</p>
      <div className="mb-6">
        {/* Prices stay LTR so currency symbols and digits keep their order. */}
        <span className="text-4xl font-display text-[#434343]" dir="ltr">{price}</span>
        <span className="text-[#434343]/60">{period}</span>
      </div>
      <Button
        onClick={onButtonClick}
        disabled={disabled}
        className={`w-full mb-6 ${
          highlighted
            ? 'bg-gradient-to-r from-[#7367FF] to-[#2DD2FE] hover:opacity-90 text-white shadow-lg shadow-[#7367FF]/30'
            : 'bg-white/60 hover:bg-white/80 text-[#434343] border border-white/40'
        }`}
      >
        {buttonText}
      </Button>
      <ul className="space-y-3">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start gap-2 text-sm text-[#434343]/80">
            <CircleCheck className="h-5 w-5 text-[#3CFF9B] flex-shrink-0 mt-0.5" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

// Testimonial Card Component
interface TestimonialCardProps {
  quote: string;
  author: string;
  role: string;
  rating: number;
}

function TestimonialCard({ quote, author, role, rating }: TestimonialCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="bg-white/60 backdrop-blur-xl border border-white/40 rounded-2xl p-6 shadow-lg"
    >
      <div className="flex gap-1 mb-4">
        {Array.from({ length: rating }).map((_, i) => (
          <Star key={i} className="h-5 w-5 fill-[#7367FF] text-[#7367FF]" />
        ))}
      </div>
      <p className="text-[#434343]/80 mb-6 italic">&ldquo;{quote}&rdquo;</p>
      <div>
        <p className="font-medium text-[#434343]">{author}</p>
        <p className="text-sm text-[#434343]/60">{role}</p>
      </div>
    </motion.div>
  );
}

// FAQ Item Component
interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
}

function FAQItem({ question, answer, isOpen, onClick }: FAQItemProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="bg-white/60 backdrop-blur-xl border border-white/40 rounded-2xl overflow-hidden shadow-lg"
    >
      <button
        onClick={onClick}
        className="w-full flex items-center justify-between p-6 text-start hover:bg-white/40 transition-colors"
      >
        <span className="font-medium text-[#434343] pe-4">{question}</span>
        <ChevronDown
          className={`h-5 w-5 text-[#7367FF] flex-shrink-0 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className="px-6 pb-6"
        >
          <p className="text-[#434343]/70">{answer}</p>
        </motion.div>
      )}
    </motion.div>
  );
}
